import type { MenuItemT } from "@/app/constants/menu-items";
import type {
  TenantLinkKeyT,
  TenantResolvedLinkT,
} from "@/app/config/tenant-link-types";
import {
  getRoutePath,
  getTenantExternalRedirects,
  getTenantSectionTemplate,
} from "@/app/config/navigation-ssot";

export type TenantKeyT =
  "wiest-group" | "autohaus-rauch" | "schuetz-gross-umstadt";

export type ExternalTenantKeyT = Exclude<TenantKeyT, "wiest-group">;

export type TenantNavbarLogoT = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

export type TenantSectionDefinitionT = {
  submenuTemplate: MenuItemT[];
};

export type TenantLocationScopeDefinitionT = {
  slugs: string[];
  fallbackNames: string[];
};

export type TenantAnalyticsConfigT = {
  /** Google Tag Manager container id (GTM-XXXXXXX). Takes precedence over gaMeasurementId. */
  gtmContainerId?: string;
  /** Direct GA4 measurement id (G-XXXXXXX). Used only when no GTM container is configured. */
  gaMeasurementId?: string;
};

export type TenantDefinitionT = {
  key: TenantKeyT;
  displayName: string;
  websiteUrlEnvVar: string;
  websiteUrlFallback: string;
  seoBaseUrlFallback: string;
  hostnameEnvVar: string;
  hostnameFallbacks: string[];
  defaultAnsprechpartnerLocation?: string;
  defaultVehicleSearchLocation?: string;
  serviceBrandLocationName?: string;
  showServiceBrandLogosInNavbar?: boolean;
  /** Make names (case-insensitive) in desired display order. Logos not listed appear at the end. */
  serviceBrandLogosOrder?: string[];
  /** Make names (case-insensitive) to hide from navbar. */
  serviceBrandLogosHidden?: string[];
  navbarLogo?: TenantNavbarLogoT;
  defaultOpenSubmenuHref?: string;
  sectionDefinition?: TenantSectionDefinitionT;
  locationScopeDefinition?: TenantLocationScopeDefinitionT;
  tenantLinks: Record<TenantLinkKeyT, TenantResolvedLinkT>;
  externalRedirects: Record<string, string>;
  crossTenantKeys: TenantKeyT[];
  /** Analytics loaders are mounted per tenant and only after analytics consent. */
  analytics?: TenantAnalyticsConfigT;
};

// Shared GA4 property used by all tenants before per-tenant GTM containers existed.
const LEGACY_SHARED_GA_MEASUREMENT_ID = "G-E2SBTDQK0Q";

const EXTERNAL_TENANT_LOCATION_SCOPE_DEFINITIONS: Record<
  ExternalTenantKeyT,
  TenantLocationScopeDefinitionT
> = {
  "autohaus-rauch": {
    slugs: ["autohaus-rauch"],
    fallbackNames: ["Autohaus Rauch Lampertheim", "Lampertheim"],
  },
  "schuetz-gross-umstadt": {
    slugs: ["auto-schuetz"],
    fallbackNames: [
      "Auto-Schütz GmbH",
      "Auto-Schuetz GmbH",
      "Schuetz-Gross-Umstadt",
      "Gross-Umstadt",
    ],
  },
};

// Local tenants host their career pages inside the same domain.
const LOCAL_CAREER_LINKS: Pick<
  Record<TenantLinkKeyT, TenantResolvedLinkT>,
  "career" | "career-training"
> = {
  career: {
    href: "/karriere",
    external: false,
  },
  "career-training": {
    href: "/karriere",
    external: false,
  },
};

const ONLINE_APPOINTMENT_LINK: TenantResolvedLinkT = {
  href: getRoutePath("online-appointments"),
  external: false,
};

// Autohaus Rauch has no online booking; appointments are arranged by phone.
const RAUCH_TENANT_LINKS: Record<TenantLinkKeyT, TenantResolvedLinkT> = {
  ...LOCAL_CAREER_LINKS,
  appointment: {
    href: getRoutePath("workshop-appointment"),
    external: false,
    title: "Werkstatttermin",
    pinToOwnDomain: true,
  },
};

const SCHUETZ_TENANT_LINKS: Record<TenantLinkKeyT, TenantResolvedLinkT> = {
  ...LOCAL_CAREER_LINKS,
  appointment: ONLINE_APPOINTMENT_LINK,
};

// Default tenant keeps legacy external career destination.
const WIEST_GROUP_TENANT_LINKS: Record<TenantLinkKeyT, TenantResolvedLinkT> = {
  career: {
    href: "https://karriere.wiest-group.de/",
    external: true,
  },
  "career-training": {
    href: "https://karriere.wiest-group.de/ausbildung/",
    external: true,
  },
  appointment: ONLINE_APPOINTMENT_LINK,
};

// Tenant definitions are the static SSOT for hostnames, links and cross-tenant structure.
export const TENANT_DEFINITIONS: Record<TenantKeyT, TenantDefinitionT> = {
  "wiest-group": {
    key: "wiest-group",
    displayName: "Wiest Group",
    websiteUrlEnvVar: "NEXT_PUBLIC_WIEST_WEBSITE_BASE_URL",
    websiteUrlFallback: "https://www.wiest-group.de",
    seoBaseUrlFallback: "https://www.wiest-group.de",
    hostnameEnvVar: "NEXT_PUBLIC_WIEST_HOSTNAMES",
    hostnameFallbacks: [
      "wiest-group.de",
      "www.wiest-group.de",
      "localhost",
      "127.0.0.1",
    ],
    tenantLinks: WIEST_GROUP_TENANT_LINKS,
    externalRedirects: getTenantExternalRedirects("wiest-group"),
    crossTenantKeys: ["autohaus-rauch", "schuetz-gross-umstadt"],
    // GA4 (G-E2SBTDQK0Q) is configured as a Google tag inside this container.
    analytics: { gtmContainerId: "GTM-T69HVPJZ" },
  },
  "autohaus-rauch": {
    key: "autohaus-rauch",
    displayName: "Autohaus Rauch",
    websiteUrlEnvVar: "NEXT_PUBLIC_RAUCH_WEBSITE_BASE_URL",
    websiteUrlFallback: "https://www.autohaus-rauch.de",
    seoBaseUrlFallback: "https://www.autohaus-rauch.de",
    hostnameEnvVar: "NEXT_PUBLIC_RAUCH_HOSTNAMES",
    hostnameFallbacks: [
      "autohaus-rauch.de",
      "www.autohaus-rauch.de",
      "autohaus-rauch.localhost",
      "www.autohaus-rauch.localhost",
    ],
    defaultAnsprechpartnerLocation: "Autohaus Rauch Lampertheim",
    defaultVehicleSearchLocation: "Lampertheim",
    serviceBrandLocationName: "Autohaus Rauch Lampertheim",
    showServiceBrandLogosInNavbar: true,
    serviceBrandLogosOrder: [
      "Audi Service",
      "Audi",
      "Seat Service",
      "Cupra Service",
      "Seat",
      "Cupra",
    ],
    serviceBrandLogosHidden: ["Volkswagen"],
    navbarLogo: {
      src: "/rauch-logo.svg",
      alt: "Autohaus Rauch",
      width: 596,
      height: 149,
    },
    sectionDefinition: getTenantSectionTemplate("autohaus-rauch"),
    locationScopeDefinition:
      EXTERNAL_TENANT_LOCATION_SCOPE_DEFINITIONS["autohaus-rauch"],
    tenantLinks: RAUCH_TENANT_LINKS,
    externalRedirects: getTenantExternalRedirects("autohaus-rauch"),
    crossTenantKeys: ["wiest-group", "schuetz-gross-umstadt"],
    analytics: { gaMeasurementId: LEGACY_SHARED_GA_MEASUREMENT_ID },
  },
  "schuetz-gross-umstadt": {
    key: "schuetz-gross-umstadt",
    displayName: "Autohaus Schütz",
    websiteUrlEnvVar: "NEXT_PUBLIC_SCHUETZ_WEBSITE_BASE_URL",
    websiteUrlFallback: "https://www.auto-schuetz.de",
    seoBaseUrlFallback: "https://www.auto-schuetz.de",
    hostnameEnvVar: "NEXT_PUBLIC_SCHUETZ_HOSTNAMES",
    hostnameFallbacks: [
      "auto-schuetz.de",
      "www.auto-schuetz.de",
      "schuetz-gross-umstadt.localhost",
      "www.schuetz-gross-umstadt.localhost",
    ],
    defaultAnsprechpartnerLocation: "Auto-Schütz GmbH",
    defaultVehicleSearchLocation: "Groß-Umstadt",
    serviceBrandLocationName: "Auto-Schütz GmbH",
    showServiceBrandLogosInNavbar: false,
    serviceBrandLogosOrder: ["Volkswagen"],
    navbarLogo: {
      src: "/logo-auto_schuetz.png",
      alt: "Auto Schütz - Partner der WIEST GROUP",
      width: 200,
      height: 60,
    },
    sectionDefinition: getTenantSectionTemplate("schuetz-gross-umstadt"),
    locationScopeDefinition:
      EXTERNAL_TENANT_LOCATION_SCOPE_DEFINITIONS["schuetz-gross-umstadt"],
    tenantLinks: SCHUETZ_TENANT_LINKS,
    externalRedirects: getTenantExternalRedirects("schuetz-gross-umstadt"),
    crossTenantKeys: ["wiest-group", "autohaus-rauch"],
    analytics: { gaMeasurementId: LEGACY_SHARED_GA_MEASUREMENT_ID },
  },
};

export const TENANT_KEYS = Object.keys(TENANT_DEFINITIONS) as TenantKeyT[];

export const DEFAULT_TENANT_KEY: TenantKeyT = "wiest-group";

export const getTenantDefinition = (tenantKey: TenantKeyT): TenantDefinitionT =>
  TENANT_DEFINITIONS[tenantKey];

export const isExternalTenantKey = (
  tenantKey: TenantKeyT
): tenantKey is ExternalTenantKeyT => tenantKey !== DEFAULT_TENANT_KEY;
